import BackToCPQButton from "../../Components/backToCPQButton";
import PreviousPageButtonCPQ from "../../Components/PreviousPageButtonCPQ";

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Button,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Box,
    Grid,
    Snackbar,
    Alert,
    CircularProgress,
    InputAdornment,
    Tooltip,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    FilterAlt as FilterIcon,
    Clear as ClearIcon,
} from "@mui/icons-material";
import apiClient from "../../Helpers/apiClient";

function JutsTransportFreightRateTable() {
    const [rates, setRates] = useState([]);
    const [filteredRates, setFilteredRates] = useState([]);
    const [freightCompanies, setFreightCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedRate, setSelectedRate] = useState(null);
    const [formData, setFormData] = useState({
        id: 0,
        freightCompanyId: "",
        stateCode: "",
        zoneName: "",
        startPostcode: "",
        endPostcode: "",
        quarterLoadRate: 0,
        halfLoadRate: 0,
        fullLoadRate: 0,
        wideLoad32Rate: 0,
        wideLoad35Rate: 0,
        wideLoad40Rate: 0,
        notes: "",
    });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("id");
    const [filterText, setFilterText] = useState("");
    const [filterField, setFilterField] = useState("all");

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        applyFilters();
    }, [rates, filterText, filterField]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [ratesResponse, companiesResponse] = await Promise.all([
                apiClient.get("/api/FreightSystem/JutsTransportFreightRates"),
                apiClient.get("/api/FreightSystem/FreightCompanies"),
            ]);

            if (Array.isArray(ratesResponse.data)) {
                setRates(ratesResponse.data);
            } else {
                setRates([]);
            }

            if (Array.isArray(companiesResponse.data)) {
                setFreightCompanies(companiesResponse.data);
            } else {
                setFreightCompanies([]);
            }

            setError(null);
        } catch (err) {
            setError(`Failed to load data: ${err.message}`);
            setRates([]);
            setFreightCompanies([]);
        } finally {
            setLoading(false);
        }
    };

    const getCompanyName = (freightCompanyId) => {
        const company = freightCompanies.find((c) => (c.id || c.Id) === freightCompanyId);
        return company ? company.companyName || company.CompanyName : "Unknown Company";
    };

    const handleOpenDialog = (rate = null) => {
        if (rate) {
            setFormData({
                id: rate.id || rate.Id || 0,
                freightCompanyId: rate.freightCompanyId || rate.FreightCompanyId || "",
                stateCode: rate.stateCode || rate.StateCode || "",
                zoneName: rate.zoneName || rate.ZoneName || "",
                startPostcode: rate.startPostcode || rate.StartPostcode || "",
                endPostcode: rate.endPostcode || rate.EndPostcode || "",
                quarterLoadRate: rate.quarterLoadRate || rate.QuarterLoadRate || 0,
                halfLoadRate: rate.halfLoadRate || rate.HalfLoadRate || 0,
                fullLoadRate: rate.fullLoadRate || rate.FullLoadRate || 0,
                wideLoad32Rate: rate.wideLoad32Rate || rate.WideLoad32Rate || 0,
                wideLoad35Rate: rate.wideLoad35Rate || rate.WideLoad35Rate || 0,
                wideLoad40Rate: rate.wideLoad40Rate || rate.WideLoad40Rate || 0,
                notes: rate.notes || rate.Notes || "",
            });
            setSelectedRate(rate);
        } else {
            setFormData({
                id: 0,
                freightCompanyId: "",
                stateCode: "",
                zoneName: "",
                startPostcode: "",
                endPostcode: "",
                quarterLoadRate: 0,
                halfLoadRate: 0,
                fullLoadRate: 0,
                wideLoad32Rate: 0,
                wideLoad35Rate: 0,
                wideLoad40Rate: 0,
                notes: "",
            });
            setSelectedRate(null);
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleOpenDeleteDialog = (rate) => {
        setSelectedRate(rate);
        setOpenDeleteDialog(true);
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setSelectedRate(null);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]:
                name === "freightCompanyId"
                    ? parseInt(value) || ""
                    : name.includes("Rate")
                    ? parseFloat(value) || 0
                    : value,
        });
    };

    const handleSubmit = async () => {
        try {
            if (!formData.freightCompanyId) {
                setSnackbar({
                    open: true,
                    message: "Please select a freight company.",
                    severity: "error",
                });
                return;
            }

            const payload = {
                Id: formData.id,
                FreightCompanyId: formData.freightCompanyId,
                StateCode: formData.stateCode,
                ZoneName: formData.zoneName,
                StartPostcode: formData.startPostcode,
                EndPostcode: formData.endPostcode,
                QuarterLoadRate: formData.quarterLoadRate,
                HalfLoadRate: formData.halfLoadRate,
                FullLoadRate: formData.fullLoadRate,
                WideLoad32Rate: formData.wideLoad32Rate,
                WideLoad35Rate: formData.wideLoad35Rate,
                WideLoad40Rate: formData.wideLoad40Rate,
                Notes: formData.notes,
            };

            if (selectedRate) {
                await apiClient.put(`/api/FreightSystem/JutsTransportFreightRates/${payload.Id}`, payload);
                setSnackbar({
                    open: true,
                    message: "Freight rate updated successfully!",
                    severity: "success",
                });
            } else {
                await apiClient.post("/api/FreightSystem/JutsTransportFreightRates", payload);
                setSnackbar({
                    open: true,
                    message: "Freight rate created successfully!",
                    severity: "success",
                });
            }

            handleCloseDialog();
            fetchData();
        } catch (err) {
            setSnackbar({
                open: true,
                message: `Error: ${err.response?.data?.message || "Failed to save. Please try again."}`,
                severity: "error",
            });
        }
    };

    const handleDelete = async () => {
        try {
            await apiClient.delete(
                `/api/FreightSystem/JutsTransportFreightRates/${selectedRate.id || selectedRate.Id}`
            );
            setSnackbar({
                open: true,
                message: "Freight rate deleted successfully!",
                severity: "success",
            });
            handleCloseDeleteDialog();
            fetchData();
        } catch (err) {
            setSnackbar({
                open: true,
                message: `Error: ${err.response?.data?.message || "Failed to delete. Please try again."}`,
                severity: "error",
            });
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const getSortedData = (data) => {
        if (!data.length) return [];

        return [...data].sort((a, b) => {
            let valueA, valueB;

            if (orderBy === "id") {
                valueA = parseInt(a.id || a.Id || 0);
                valueB = parseInt(b.id || b.Id || 0);
                return order === "asc" ? valueA - valueB : valueB - valueA;
            } else if (orderBy === "companyName") {
                valueA = getCompanyName(a.freightCompanyId || a.FreightCompanyId);
                valueB = getCompanyName(b.freightCompanyId || b.FreightCompanyId);
            } else if (orderBy === "stateCode") {
                valueA = a.stateCode || a.StateCode || "";
                valueB = b.stateCode || b.StateCode || "";
            } else if (orderBy === "zoneName") {
                valueA = a.zoneName || a.ZoneName || "";
                valueB = b.zoneName || b.ZoneName || "";
            } else if (orderBy.includes("Rate")) {
                const propName = orderBy.charAt(0).toUpperCase() + orderBy.slice(1);
                valueA = parseFloat(a[orderBy] || a[propName] || 0);
                valueB = parseFloat(b[orderBy] || b[propName] || 0);
                return order === "asc" ? valueA - valueB : valueB - valueA;
            }

            if (typeof valueA !== "number") {
                valueA = String(valueA || "").toLowerCase();
                valueB = String(valueB || "").toLowerCase();
                return order === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
            }

            return order === "asc" ? valueA - valueB : valueB - valueA;
        });
    };

    const handleFilterChange = (event) => {
        setFilterText(event.target.value);
    };

    const handleFilterFieldChange = (field) => {
        setFilterField(field);
    };

    const clearFilter = () => {
        setFilterText("");
        setFilterField("all");
    };

    const applyFilters = () => {
        if (!filterText.trim()) {
            setFilteredRates(rates);
            return;
        }

        const searchTerm = filterText.toLowerCase().trim();

        const filtered = rates.filter((rate) => {
            const stateCode = rate.stateCode || rate.StateCode || "";
            const zoneName = rate.zoneName || rate.ZoneName || "";
            const startPostcode = rate.startPostcode || rate.StartPostcode || "";
            const endPostcode = rate.endPostcode || rate.EndPostcode || "";
            const notes = rate.notes || rate.Notes || "";
            const companyName = getCompanyName(rate.freightCompanyId || rate.FreightCompanyId);

            if (filterField === "all") {
                return (
                    String(rate.id || rate.Id || "").includes(searchTerm) ||
                    companyName.toLowerCase().includes(searchTerm) ||
                    stateCode.toLowerCase().includes(searchTerm) ||
                    zoneName.toLowerCase().includes(searchTerm) ||
                    startPostcode.toLowerCase().includes(searchTerm) ||
                    endPostcode.toLowerCase().includes(searchTerm) ||
                    notes.toLowerCase().includes(searchTerm)
                );
            }

            if (filterField === "id") return String(rate.id || rate.Id || "").includes(searchTerm);
            if (filterField === "companyName") return companyName.toLowerCase().includes(searchTerm);
            if (filterField === "stateCode") return stateCode.toLowerCase().includes(searchTerm);
            if (filterField === "zoneName") return zoneName.toLowerCase().includes(searchTerm);
            if (filterField === "postcode")
                return (
                    startPostcode.toLowerCase().includes(searchTerm) ||
                    endPostcode.toLowerCase().includes(searchTerm)
                );

            return false;
        });

        setFilteredRates(filtered);
    };

    const displayData = getSortedData(filteredRates);

    return (
        <Box
            sx={{
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                padding: 4,
                paddingTop: 2,
                boxSizing: "border-box",
                overflow: "hidden",
            }}
        >
            <Typography variant="h4" gutterBottom align="center">
                Juts Transport Freight Rate Table
            </Typography>

            <Paper
                sx={{
                    width: "100%",
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    mb: 2,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        p: 2,
                        alignItems: "center",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "60%",
                        }}
                    >
                        <TextField
                            variant="outlined"
                            placeholder="Search..."
                            size="small"
                            fullWidth
                            value={filterText}
                            onChange={handleFilterChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: filterText ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={clearFilter} edge="end">
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                        <Tooltip title="Filter by field">
                            <Box>
                                <IconButton
                                    color={filterField !== "all" ? "primary" : "default"}
                                    onClick={(e) => {
                                        e.currentTarget.nextElementSibling.style.display =
                                            e.currentTarget.nextElementSibling.style.display === "none"
                                                ? "flex"
                                                : "none";
                                    }}
                                >
                                    <FilterIcon />
                                </IconButton>
                                <Box
                                    sx={{
                                        position: "absolute",
                                        zIndex: 10,
                                        bgcolor: "background.paper",
                                        boxShadow: 3,
                                        borderRadius: 1,
                                        p: 1,
                                        display: "none",
                                        flexDirection: "column",
                                        gap: 0.5,
                                    }}
                                >
                                    {[
                                        { id: "all", label: "All Fields" },
                                        { id: "id", label: "ID" },
                                        { id: "companyName", label: "Company" },
                                        { id: "stateCode", label: "State" },
                                        { id: "zoneName", label: "Zone" },
                                        { id: "postcode", label: "Postcode" },
                                    ].map((option) => (
                                        <Button
                                            key={option.id}
                                            size="small"
                                            variant={filterField === option.id ? "contained" : "text"}
                                            onClick={() => {
                                                handleFilterFieldChange(option.id);
                                                document.querySelector(
                                                    '[aria-label="Filter options"]'
                                                ).nextElementSibling.style.display = "none";
                                            }}
                                        >
                                            {option.label}
                                        </Button>
                                    ))}
                                </Box>
                            </Box>
                        </Tooltip>
                    </Box>

                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                        Add New Rate
                    </Button>
                </Box>

                <Divider />

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                ) : (
                    <>
                        <TableContainer
                            sx={{
                                flex: 1,
                                overflow: "auto",
                            }}
                        >
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "id"}
                                                direction={orderBy === "id" ? order : "asc"}
                                                onClick={() => handleRequestSort("id")}
                                            >
                                                <strong>ID</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "companyName"}
                                                direction={orderBy === "companyName" ? order : "asc"}
                                                onClick={() => handleRequestSort("companyName")}
                                            >
                                                <strong>Company</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "stateCode"}
                                                direction={orderBy === "stateCode" ? order : "asc"}
                                                onClick={() => handleRequestSort("stateCode")}
                                            >
                                                <strong>State</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "zoneName"}
                                                direction={orderBy === "zoneName" ? order : "asc"}
                                                onClick={() => handleRequestSort("zoneName")}
                                            >
                                                <strong>Zone</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <strong>Postcode Range</strong>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "quarterLoadRate"}
                                                direction={orderBy === "quarterLoadRate" ? order : "asc"}
                                                onClick={() => handleRequestSort("quarterLoadRate")}
                                            >
                                                <strong>Quarter Load</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "halfLoadRate"}
                                                direction={orderBy === "halfLoadRate" ? order : "asc"}
                                                onClick={() => handleRequestSort("halfLoadRate")}
                                            >
                                                <strong>Half Load</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "fullLoadRate"}
                                                direction={orderBy === "fullLoadRate" ? order : "asc"}
                                                onClick={() => handleRequestSort("fullLoadRate")}
                                            >
                                                <strong>Full Load</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center">
                                            <strong>Actions</strong>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {displayData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={9} align="center">
                                                {filterText
                                                    ? "No matches found. Try a different search."
                                                    : "No data available. Add your first freight rate."}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        displayData.map((rate) => (
                                            <TableRow key={rate.id || rate.Id} hover>
                                                <TableCell>{rate.id || rate.Id}</TableCell>
                                                <TableCell>
                                                    {getCompanyName(
                                                        rate.freightCompanyId || rate.FreightCompanyId
                                                    )}
                                                </TableCell>
                                                <TableCell>{rate.stateCode || rate.StateCode}</TableCell>
                                                <TableCell>{rate.zoneName || rate.ZoneName}</TableCell>
                                                <TableCell>
                                                    {(rate.startPostcode || rate.StartPostcode) &&
                                                    (rate.endPostcode || rate.EndPostcode)
                                                        ? `${rate.startPostcode || rate.StartPostcode} - ${
                                                              rate.endPostcode || rate.EndPostcode
                                                          }`
                                                        : rate.startPostcode ||
                                                          rate.StartPostcode ||
                                                          rate.endPostcode ||
                                                          rate.EndPostcode ||
                                                          ""}
                                                </TableCell>
                                                <TableCell>
                                                    $
                                                    {(
                                                        rate.quarterLoadRate ||
                                                        rate.QuarterLoadRate ||
                                                        0
                                                    ).toFixed(2)}
                                                </TableCell>
                                                <TableCell>
                                                    $
                                                    {(rate.halfLoadRate || rate.HalfLoadRate || 0).toFixed(2)}
                                                </TableCell>
                                                <TableCell>
                                                    $
                                                    {(rate.fullLoadRate || rate.FullLoadRate || 0).toFixed(2)}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => handleOpenDialog(rate)}
                                                        size="small"
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleOpenDeleteDialog(rate)}
                                                        size="small"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </Paper>

            <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                <BackToCPQButton />
                <PreviousPageButtonCPQ />
            </Box>

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
                <DialogTitle>{selectedRate ? "Edit Freight Rate" : "Add New Freight Rate"}</DialogTitle>
                <DialogContent>
                    <Box component="form" sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth required>
                                    <InputLabel>Freight Company</InputLabel>
                                    <Select
                                        name="freightCompanyId"
                                        value={formData.freightCompanyId}
                                        onChange={handleFormChange}
                                        label="Freight Company"
                                    >
                                        {freightCompanies.map((company) => (
                                            <MenuItem
                                                key={company.id || company.Id}
                                                value={company.id || company.Id}
                                            >
                                                {company.companyName || company.CompanyName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    label="State Code"
                                    name="stateCode"
                                    value={formData.stateCode}
                                    onChange={handleFormChange}
                                    variant="outlined"
                                    helperText="e.g., NSW, VIC"
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    label="Zone Name"
                                    name="zoneName"
                                    value={formData.zoneName}
                                    onChange={handleFormChange}
                                    variant="outlined"
                                    helperText="e.g., Metro, Regional"
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    label="Start Postcode"
                                    name="startPostcode"
                                    value={formData.startPostcode}
                                    onChange={handleFormChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    label="End Postcode"
                                    name="endPostcode"
                                    value={formData.endPostcode}
                                    onChange={handleFormChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Quarter Load Rate"
                                    name="quarterLoadRate"
                                    type="number"
                                    inputProps={{ step: "0.01", min: "0" }}
                                    value={formData.quarterLoadRate}
                                    onChange={handleFormChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Half Load Rate"
                                    name="halfLoadRate"
                                    type="number"
                                    inputProps={{ step: "0.01", min: "0" }}
                                    value={formData.halfLoadRate}
                                    onChange={handleFormChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Full Load Rate"
                                    name="fullLoadRate"
                                    type="number"
                                    inputProps={{ step: "0.01", min: "0" }}
                                    value={formData.fullLoadRate}
                                    onChange={handleFormChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Wide Load 32 Rate"
                                    name="wideLoad32Rate"
                                    type="number"
                                    inputProps={{ step: "0.01", min: "0" }}
                                    value={formData.wideLoad32Rate}
                                    onChange={handleFormChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Wide Load 35 Rate"
                                    name="wideLoad35Rate"
                                    type="number"
                                    inputProps={{ step: "0.01", min: "0" }}
                                    value={formData.wideLoad35Rate}
                                    onChange={handleFormChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Wide Load 40 Rate"
                                    name="wideLoad40Rate"
                                    type="number"
                                    inputProps={{ step: "0.01", min: "0" }}
                                    value={formData.wideLoad40Rate}
                                    onChange={handleFormChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Notes"
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleFormChange}
                                    variant="outlined"
                                    multiline
                                    rows={3}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary">
                        {selectedRate ? "Update" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this freight rate? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default JutsTransportFreightRateTable;
